const SEMVER_PATTERN =
  /^(0|[1-9]\d*)\.(0|[1-9]\d*)\.(0|[1-9]\d*)(?:-([0-9A-Za-z-]+(?:\.[0-9A-Za-z-]+)*))?(?:\+[0-9A-Za-z-]+(?:\.[0-9A-Za-z-]+)*)?$/;

interface ParsedSdkVersion {
  readonly major: number;
  readonly minor: number;
  readonly patch: number;
  readonly prerelease: readonly string[];
}

export class InvalidSdkVersionError extends Error {
  readonly code = 'invalid_sdk_version';

  constructor(version: string) {
    super(`Invalid SDK semantic version: ${version}`);
    this.name = 'InvalidSdkVersionError';
  }
}

function parseSdkVersion(version: string): ParsedSdkVersion | null {
  const match = SEMVER_PATTERN.exec(version);
  if (!match) {
    return null;
  }

  const major = Number(match[1]);
  const minor = Number(match[2]);
  const patch = Number(match[3]);
  const prerelease = match[4]?.split('.') ?? [];

  if (
    !Number.isSafeInteger(major) ||
    !Number.isSafeInteger(minor) ||
    !Number.isSafeInteger(patch) ||
    prerelease.some(
      (identifier) =>
        identifier.length === 0 ||
        (/^\d+$/.test(identifier) &&
          identifier.length > 1 &&
          identifier.startsWith('0')),
    )
  ) {
    return null;
  }

  return { major, minor, patch, prerelease };
}

export function isCanonicalSdkVersion(value: unknown): value is string {
  return typeof value === 'string' && parseSdkVersion(value) !== null;
}

function comparePrerelease(
  left: readonly string[],
  right: readonly string[],
): -1 | 0 | 1 {
  if (left.length === 0 && right.length === 0) {
    return 0;
  }
  if (left.length === 0) {
    return 1;
  }
  if (right.length === 0) {
    return -1;
  }

  const length = Math.max(left.length, right.length);
  for (let index = 0; index < length; index += 1) {
    const leftPart = left[index];
    const rightPart = right[index];

    if (leftPart === undefined) return -1;
    if (rightPart === undefined) return 1;
    if (leftPart === rightPart) continue;

    const leftNumeric = /^\d+$/.test(leftPart);
    const rightNumeric = /^\d+$/.test(rightPart);

    if (leftNumeric && rightNumeric) {
      if (leftPart.length !== rightPart.length) {
        return leftPart.length < rightPart.length ? -1 : 1;
      }
      return leftPart < rightPart ? -1 : 1;
    }
    if (leftNumeric) return -1;
    if (rightNumeric) return 1;
    return leftPart < rightPart ? -1 : 1;
  }

  return 0;
}

export function compareSdkVersions(left: string, right: string): -1 | 0 | 1 {
  const parsedLeft = parseSdkVersion(left);
  const parsedRight = parseSdkVersion(right);

  if (!parsedLeft) throw new InvalidSdkVersionError(left);
  if (!parsedRight) throw new InvalidSdkVersionError(right);

  for (const key of ['major', 'minor', 'patch'] as const) {
    if (parsedLeft[key] < parsedRight[key]) return -1;
    if (parsedLeft[key] > parsedRight[key]) return 1;
  }

  return comparePrerelease(parsedLeft.prerelease, parsedRight.prerelease);
}

export function isSdkVersionAtLeast(
  currentVersion: string,
  minimumVersion: string,
): boolean {
  return compareSdkVersions(currentVersion, minimumVersion) >= 0;
}
